import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Download, FileText, AlertTriangle, CheckCircle2,
  Calendar, Target, ShieldCheck, ChevronRight, Info,
} from 'lucide-react';
import { Button, Pill, Card, EmptyState } from '../../index';
import { TrustGauge } from '../TrustGauge';
import { getTrustBoardReport, downloadTrustBoardReport } from '../../../../services/api';

/**
 * BoardReportTab — V2 CISO Board Report (Phase A7 / G307).
 *
 * Full V1 feature parity:
 *   - Header: title, generated date, date range selector, export
 *     dropdown (DOCX / PPTX / JSON raw)
 *   - Optional Board Note (amber alert card)
 *   - Trust Gauge (SVG, semicircular w/ target needle) + Fleet Health
 *   - 3x2 quick metrics grid (Total / High Risk / Anomalies / On Probation / Meets Target / Risk Tolerance)
 *   - Executive Summary narrative
 *   - Organisational Trust Goals (4-col grid + progress bar)
 *   - Agent Risk Distribution (4 colored cards: LOW/MEDIUM/HIGH/CRITICAL)
 *   - Agents Requiring Attention (expandable risk cards w/ business risk + recommended actions + anomaly badges)
 *   - Fleet-Level Recommendations (priority pill + category + text)
 *   - Risk Level Definitions table
 *
 * The board report is tenant-wide by design — scope toggle does not
 * filter, but date range selection drives the data window.
 *
 * @param {Object} props
 * @param {Function} props.addToast
 */
/**
 * @param {Object} props
 * @param {string} [props.scope] — the archetype population the Trust Center is filtered to.
 *   Threaded to the backend rather than applied here: the DOCX, PPTX and PDF are rendered server
 *   side from the same report, so filtering in the browser would have left the exported pack
 *   describing a different fleet from the one on screen.
 */
export function BoardReportTab({ scope = 'all', addToast }) {
  const [dateRange, setDateRange] = useState('30d');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef(null);
  const [expandedAgents, setExpandedAgents] = useState(() => new Set());

  /* Load report. Re-runs whenever the date range changes. */
  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getTrustBoardReport({ dateRange, scope });
      if (r?.ok === false) {
        setError(r.error || 'Failed to load report.');
        setReport(null);
      } else {
        setReport(r?.data || r);
      }
    } catch (e) {
      setError(e?.message || 'Failed to load report.');
      setReport(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateRange, scope]);

  /* Click-outside to close the export menu — common UX pattern but
   * the v2 MenuDropdown isn't suited for tri-format export, so we
   * roll a tiny dropdown here. */
  useEffect(() => {
    if (!exportMenuOpen) return;
    const onDocClick = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [exportMenuOpen]);

  const handleExport = async (format) => {
    setExportMenuOpen(false);
    if (format === 'json') {
      try {
        const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `trust-board-report-${scope}-${dateRange}-${new Date().toISOString().slice(0, 10)}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        addToast?.('Board report JSON downloaded.', 'success');
      } catch (e) {
        addToast?.(`JSON export failed: ${e?.message || e}`, 'error');
      }
      return;
    }
    setExporting(format);
    try {
      const r = await downloadTrustBoardReport(format, { dateRange, scope });
      if (r?.ok) addToast?.(`Board report (${format.toUpperCase()}) downloaded.`, 'success');
      else addToast?.(`Export failed: ${r?.error || 'unknown error'}`, 'error');
    } finally { setExporting(null); }
  };

  /* ── Derived data (G323) ──────────────────────────────────────────
   *
   * The backend response shape comes from
   * AgentTrustCenterService.generateBoardReport. The CORRECT field paths
   * are emitted by Jackson tree under specific nested objects (not the
   * flat shapes V2 previously assumed). Defensive fallbacks cover any
   * legacy / partial shapes so older deploys still render something. */
  const goals = report?.organizationalGoals || report?.orgGoals || {};
  const fleetMetrics = report?.fleetMetrics || report?.summary || {};
  const thresholds = report?.scoreThresholds || {};
  const transitionSummary = report?.transitionSummary || {};

  /* Risk distribution: backend emits at fleetMetrics.riskBreakdown
   * (canonical) or summary.riskDistribution (legacy). Accept both. */
  const riskDist = fleetMetrics.riskBreakdown
                || report?.riskDistribution
                || fleetMetrics.riskDistribution
                || {};

  /* Top risk agents: ARRAY at root. Per-agent fields use
   * total* (not just blocks/warns) and `remediations` (not
   * `recommendedActions`). */
  const topRisk = report?.topRiskAgents || report?.agentsRequiringAttention || [];
  const recommendations = report?.recommendations || report?.fleetRecommendations || [];

  /* riskDefinitions is an OBJECT keyed by level (LOW / MEDIUM / HIGH /
   * CRITICAL), each with {label, meaning, action}. V2 previously treated
   * this as an array, so the table never rendered. Normalise to an
   * array for display. */
  const riskDefinitionsRaw = report?.riskDefinitions || report?.riskLevelDefinitions || {};
  const riskDefinitions = Array.isArray(riskDefinitionsRaw)
    ? riskDefinitionsRaw
    : Object.entries(riskDefinitionsRaw).map(([level, def]) => ({
        level,
        label: def?.label || level,
        meaning: def?.meaning || def?.description,
        recommendedAction: def?.action || def?.recommendedAction,
      }));

  const executiveSummary = report?.executiveSummary || fleetMetrics?.narrative || null;

  /* averageScore is the canonical name; fleetTrustScore is legacy. */
  const fleetTrustScore = (() => {
    const raw = fleetMetrics.averageScore ?? fleetMetrics.fleetTrustScore ?? report?.fleetTrustScore;
    return raw == null ? null : Number(raw);
  })();
  const targetScore = (() => {
    const raw = fleetMetrics.targetScore ?? goals.targetTrustScore ?? goals.target;
    return raw == null ? null : Number(raw);
  })();
  const meetsTarget = fleetMetrics.meetsTarget
                    ?? (fleetTrustScore != null && targetScore != null && fleetTrustScore >= targetScore);

  /* How the fleet sits against the target the customer configured — a different question from
   * the one the band answers, and thresholded on their own target rather than on the score
   * ladder. The backend sends both a key (EXCELLENT / GOOD / NEEDS_ATTENTION / AT_RISK /
   * CRITICAL) and a label that says what it measures; the label is what belongs on a board page,
   * because "EXCELLENT" beside an "Adequate" dial put two vocabularies on one screen. */
  const healthGrade = fleetMetrics.healthGrade || null;
  const healthGradeLabel = fleetMetrics.healthGradeLabel
    || (healthGrade ? healthGrade.replace(/_/g, ' ') : null);
  const healthExplanation = fleetMetrics.healthExplanation || null;

  /* Board note now lives under organizationalGoals.boardReportNote. */
  const boardNote = goals.boardReportNote || report?.boardReportNote || report?.boardNote || null;

  const dateRangeLabel = dateRange === '7d' ? 'Last 7 days' : dateRange === '90d' ? 'Last 90 days' : 'Last 30 days';

  const toggleAgentExpand = (id) => setExpandedAgents((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  return (
    <div className="space-y-5">
      {/* Header card */}
      <Card padding="md">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <h2 className="text-[18px] font-bold text-v2-ink m-0">AI Agent Fleet — Board Trust Report</h2>
            <p className="text-[12px] text-v2-ink-secondary mt-0.5 m-0">
              Generated {new Date().toLocaleString()} · {dateRangeLabel}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}
                    className="border border-v2-border-subtle rounded-v2-sm px-2.5 py-1.5 bg-v2-surface text-[12.5px] focus:border-v2-action focus:outline-none">
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
            {/* Export dropdown */}
            <div className="relative" ref={exportMenuRef}>
              <Button variant="primary" size="sm" iconLeft={<Download size={13} />}
                      onClick={() => setExportMenuOpen((v) => !v)}
                      disabled={!report || !!exporting}>
                {exporting ? `Exporting ${exporting.toUpperCase()}…` : 'Export'}
              </Button>
              {exportMenuOpen && (
                <div className="absolute right-0 mt-1 w-64 bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-lg z-30 overflow-hidden">
                  <ExportOption
                    label="Word document (.docx)"
                    description="Formatted prose for inclusion in board packs"
                    onClick={() => handleExport('docx')}
                  />
                  <ExportOption
                    label="PowerPoint (.pptx)"
                    description="One-slide-per-section deck for board presentations"
                    onClick={() => handleExport('pptx')}
                  />
                  <ExportOption
                    label="PDF"
                    description="One file to send to a reviewer"
                    onClick={() => handleExport('pdf')}
                  />
                  <ExportOption
                    label="JSON (raw data)"
                    description="Machine-readable export for downstream tooling"
                    onClick={() => handleExport('json')}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      {loading && (
        <Card padding="md">
          <p className="text-v2-ink-muted text-[12.5px] m-0">Composing board report…</p>
        </Card>
      )}
      {error && (
        <Card padding="md">
          <div className="bg-v2-danger-soft border border-v2-danger/30 rounded-v2-sm px-3 py-2 text-[12.5px] text-v2-danger-strong">
            {error}
          </div>
        </Card>
      )}

      {/* RC-83a — No-tenant-binding banner.
          When the BE returned the RC-82b scaffold (reason='no-tenant-binding'),
          render an explicit "your session has no tenant scope active" banner
          instead of letting the operator stare at a sea of zeros / em-dashes
          and conclude the page is broken. The banner is also a deliberate
          short-circuit: we do NOT render the gauge + KPI strip + breakdown
          tables below because every value would be 0/em-dash. Once the
          operator switches into a customer (the header chip with the
          customer/org/tenant pickers) the next fetch returns real data
          and this banner self-clears. */}
      {!loading && !error && report?.reason === 'no-tenant-binding' && (
        <Card padding="md">
          <div className="bg-v2-warning-soft border border-v2-warning/30 rounded-v2-sm px-3 py-2.5 text-[12.5px] text-v2-ink">
            <div className="font-semibold text-v2-warning mb-1">No tenant scope is active on this session</div>
            <p className="m-0 text-v2-ink leading-relaxed">
              {report.message || 'Switch into a customer (top header) to see a populated board report. Cross-tenant aggregation for Platform Admin is on the roadmap.'}
            </p>
          </div>
        </Card>
      )}

      {!loading && !error && report && report?.reason !== 'no-tenant-binding' && (
        <>
          {/* G323 — Board note now uses the consolidated `boardNote`
              derived field so it surfaces regardless of whether the
              backend put it at goals.boardReportNote or root level. */}
          {boardNote && (
            <Card padding="md" className="border-l-4 border-v2-warning bg-v2-warning-soft/30">
              <div className="flex items-start gap-3">
                <Info size={18} className="text-v2-warning shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-[13px] font-semibold text-v2-warning m-0 mb-1">Board note</h3>
                  <p className="text-[12.5px] text-v2-ink m-0">{boardNote}</p>
                </div>
              </div>
            </Card>
          )}

          {/* G323 — Gauge + quick metrics: reads from fleetMetrics (the
              real backend path), no longer from `summary`. Adds the
              healthGrade pill + healthExplanation paragraph the
              backend computes but V2 wasn't surfacing. */}
          <Card padding="md">
            <div className="grid gap-6" style={{ gridTemplateColumns: 'auto 1fr' }}>
              <div className="flex flex-col items-center justify-center pr-6 border-r border-v2-border-subtle">
                <TrustGauge
                  score={fleetTrustScore ?? 0}
                  target={targetScore}
                  size={220}
                  label="Fleet Health"
                />
                {healthGrade && (
                  <div className="mt-2">
                    <Pill
                      tone={healthGrade === 'EXCELLENT' || healthGrade === 'GOOD' ? 'success'
                          : healthGrade === 'NEEDS_ATTENTION' ? 'warning' : 'danger'}
                      size="sm" dot
                    >
                      {healthGradeLabel}
                    </Pill>
                  </div>
                )}
              </div>
              <div className="flex flex-col">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 flex-1">
                  <QuickMetric label="Total agents" value={fleetMetrics.totalAgents ?? '—'} />
                  {/* Fleet Health is a mean, and a mean over a handful of a large fleet is a
                      different claim from one over all of it. The board reads both or neither. */}
                  <QuickMetric
                    label="Rated"
                    value={fleetMetrics.ratedAgents != null && fleetMetrics.totalAgents != null
                      ? `${fleetMetrics.ratedAgents} of ${fleetMetrics.totalAgents}`
                      : '—'}
                    tone={fleetMetrics.ratedAgents === 0 ? 'muted' : undefined} />
                  <QuickMetric label="High risk"
                               value={fleetMetrics.highRiskCount
                                    ?? ((Number(riskDist.HIGH) || 0) + (Number(riskDist.CRITICAL) || 0))}
                               tone={(fleetMetrics.highRiskCount > 0 || riskDist.HIGH || riskDist.CRITICAL) ? 'danger' : 'success'} />
                  <QuickMetric label="Active anomalies"
                               value={fleetMetrics.anomaliesDetected
                                    ?? fleetMetrics.activeAnomalies
                                    ?? '—'}
                               tone={(fleetMetrics.anomaliesDetected || fleetMetrics.activeAnomalies) > 0 ? 'danger' : 'success'} />
                  <QuickMetric label="On probation"
                               value={fleetMetrics.agentsInProbation
                                    ?? fleetMetrics.onProbation
                                    ?? '—'}
                               tone={(fleetMetrics.agentsInProbation || fleetMetrics.onProbation) > 0 ? 'danger' : 'muted'} />
                  <QuickMetric label="Meets target"
                               value={meetsTarget != null ? (meetsTarget ? 'Yes' : 'No') : '—'}
                               tone={meetsTarget ? 'success' : 'warning'} />
                  <QuickMetric label="Risk tolerance"
                               value={goals.orgRiskTolerance || goals.riskTolerance || '—'} />
                </div>
                {healthExplanation && (
                  <p className="text-[12px] text-v2-ink-secondary mt-3 m-0 pt-3 border-t border-v2-border-subtle">
                    <span className="font-semibold text-v2-ink">Health summary:</span> {healthExplanation}
                  </p>
                )}
              </div>
            </div>
          </Card>

          {/* G323 — Transition summary surfaces the autonomy-transition
              counts the backend computes (auto / manual_override / etc).
              Previously hidden. */}
          {transitionSummary && Object.keys(transitionSummary).length > 0 && (
            <Card padding="md">
              <Card.Header
                title="Autonomy transitions"
                subtitle={`${transitionSummary.total || 0} transition${transitionSummary.total === 1 ? '' : 's'} in window, by trigger type`}
              />
              <Card.Body>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {Object.entries(transitionSummary)
                    .filter(([k]) => k !== 'total')
                    .map(([trigger, count]) => (
                      <QuickMetric key={trigger}
                                   label={String(trigger).replace(/_/g, ' ').toLowerCase()}
                                   value={count} />
                    ))}
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Executive summary */}
          {executiveSummary && (
            <Card padding="md">
              <Card.Header
                title="Executive summary"
                subtitle={`Plain-language narrative covering ${dateRangeLabel.toLowerCase()}`}
              />
              <Card.Body>
                <ExecutiveSummary text={executiveSummary} />
              </Card.Body>
            </Card>
          )}

          {/* Organisational trust goals */}
          {Object.keys(goals).length > 0 && (
            <Card padding="md">
              <Card.Header
                title="Organisational trust goals"
                subtitle="Set under Trust Center → Scoring → Org goals"
              />
              <Card.Body>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <GoalCell icon={Target} label="Target score" value={goals.targetTrustScore || goals.target || '—'} />
                  <GoalCell icon={ShieldCheck} label="Target autonomy"
                            value={goals.targetAutonomyLevel || goals.targetAutonomy || '—'} />
                  <GoalCell icon={AlertTriangle} label="Risk tolerance"
                            value={goals.orgRiskTolerance || goals.riskTolerance || '—'} />
                  <GoalCell icon={Calendar} label="Compliance deadline"
                            value={goals.complianceDeadline
                              ? new Date(goals.complianceDeadline).toLocaleDateString()
                              : '—'} />
                </div>
                {fleetTrustScore != null && targetScore != null && (
                  <div className="mt-4">
                    <div className="flex items-baseline justify-between text-[12px] mb-1.5">
                      <span className="font-medium text-v2-ink">Progress to target</span>
                      <span className="text-v2-ink-muted font-mono-v2">
                        {fleetTrustScore} / {targetScore}
                      </span>
                    </div>
                    <div className="h-2.5 rounded-v2-pill bg-v2-surface-muted overflow-hidden">
                      <div className={`h-full transition-[width] duration-500 ${
                        meetsTarget ? 'bg-v2-success' : 'bg-v2-warning'
                      }`} style={{ width: `${Math.min(100, (fleetTrustScore / targetScore) * 100)}%` }} />
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Risk distribution cards */}
          <Card padding="md">
            <Card.Header
              title="Agent risk distribution"
              subtitle="Across the tenant, derived from the scoring engine's risk categorisation"
            />
            <Card.Body>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { key: 'LOW',      label: 'Low',      desc: 'Within target; nothing required' },
                  { key: 'MEDIUM',   label: 'Medium',   desc: 'Monitor; trend may worsen' },
                  { key: 'HIGH',     label: 'High',     desc: 'Active investigation recommended' },
                  { key: 'CRITICAL', label: 'Critical', desc: 'Immediate operator action' },
                ].map(({ key, label, desc }) => {
                  const n = riskDist[key] ?? 0;
                  const total = (riskDist.LOW || 0) + (riskDist.MEDIUM || 0) + (riskDist.HIGH || 0) + (riskDist.CRITICAL || 0);
                  const pct = total > 0 ? Math.round((n / total) * 100) : 0;
                  const tone = key === 'LOW' ? 'success' : key === 'MEDIUM' ? 'warning'
                             : key === 'HIGH' ? 'danger' : 'danger';
                  return (
                    <div key={key} className={`rounded-v2-md p-3 border-l-4 ${
                      tone === 'success' ? 'border-v2-success bg-v2-success-soft'
                      : tone === 'warning' ? 'border-v2-warning bg-v2-warning-soft'
                      : 'border-v2-danger bg-v2-danger-soft'
                    }`}>
                      <div className="flex items-baseline justify-between mb-1">
                        <span className={`text-[11px] font-semibold uppercase tracking-wider ${
                          tone === 'success' ? 'text-v2-success-strong'
                          : tone === 'warning' ? 'text-v2-warning-strong'
                          : 'text-v2-danger-strong'
                        }`}>{label}</span>
                        <span className="text-v2-ink-muted text-[10.5px] font-mono-v2">{pct}%</span>
                      </div>
                      <div className="text-[26px] font-bold text-v2-ink leading-none">{n}</div>
                      <p className="text-[11px] text-v2-ink-secondary mt-1 m-0">{desc}</p>
                    </div>
                  );
                })}
              </div>
            </Card.Body>
          </Card>

          {/* Agents requiring attention */}
          <Card padding="md">
            <Card.Header
              title="Agents requiring attention"
              subtitle="High-risk agents the operator should review or remediate"
            />
            <Card.Body>
              {topRisk.length === 0 ? (
                <EmptyState
                  icon={<CheckCircle2 size={20} />}
                  title="All agents within acceptable parameters"
                  description="No agents currently require manual review."
                />
              ) : (
                <ul className="space-y-2 m-0 p-0 list-none">
                  {topRisk.map((a, i) => {
                    const id = a.agentId || a.id || `agent-${i}`;
                    const isOpen = expandedAgents.has(id);
                    const risk = (a.riskLevel || 'HIGH').toUpperCase();
                    return (
                      <li key={id} className="bg-v2-surface border border-v2-border-subtle rounded-v2-md overflow-hidden">
                        <button
                          onClick={() => toggleAgentExpand(id)}
                          className="w-full text-left px-4 py-3 hover:bg-v2-surface-muted flex items-center gap-3"
                        >
                          <Pill tone={risk === 'CRITICAL' || risk === 'HIGH' ? 'danger' : 'warning'} size="sm" dot>{risk}</Pill>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-v2-ink text-[13px] truncate">{a.agentName || a.name || id}</div>
                            <div className="text-[11px] text-v2-ink-muted truncate">
                              {/* G323 — backend emits totalBlocks/totalWarnings + agentType.
                                  Old V2 read blocks/warns/status which never resolved. */}
                              {a.autonomyLevel || a.autonomy || '—'}
                              {a.agentType && <> · {a.agentType}</>}
                              {((a.totalBlocks ?? a.blocks) > 0 || (a.totalWarnings ?? a.warns) > 0) && (
                                <> · {a.totalBlocks ?? a.blocks ?? 0} blocks · {a.totalWarnings ?? a.warns ?? 0} warns</>
                              )}
                              {a.scoreGap != null && Number(a.scoreGap) > 0 && (
                                <> · <span className="text-v2-danger">−{Number(a.scoreGap).toFixed(1)} from target</span></>
                              )}
                            </div>
                          </div>
                          {/* The backend topRiskAgents node emits the score as `trustScore`
                              ("Unrated" or a numeric string), NOT `complianceScore` — reading
                              the wrong field made every row fall back to "Unrated" even though
                              the sub-line (scoreGap/blocks, which DO match field names) showed
                              real evidence. Read trustScore; show the rounded number when rated. */}
                          {(() => {
                            const ts = a.trustScore ?? a.complianceScore;
                            const n = Number(ts);
                            const rated = ts != null && ts !== 'Unrated' && Number.isFinite(n);
                            return (
                              <div className="text-[18px] font-bold text-v2-ink">
                                {rated ? Math.round(n) : 'Unrated'}
                              </div>
                            );
                          })()}
                          <ChevronRight size={14} className={`text-v2-ink-muted transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                        </button>
                        {isOpen && (
                          <div className="px-4 pb-4 pt-1 border-t border-v2-border-subtle bg-v2-surface-muted/40">
                            {/* G323 — `plainEnglish` is the backend's per-agent narrative
                                (what this means, why it matters). V2 wasn't surfacing it. */}
                            {a.plainEnglish && (
                              <p className="text-[12.5px] text-v2-ink mt-3 mb-3 m-0">{a.plainEnglish}</p>
                            )}
                            {a.description && !a.plainEnglish && (
                              <p className="text-[12.5px] text-v2-ink mt-3 mb-3 m-0">{a.description}</p>
                            )}
                            {a.businessRisk && (
                              <div className="mb-3">
                                <span className="text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted block mb-1">
                                  Business risk
                                </span>
                                <p className="text-[12.5px] text-v2-ink m-0">{a.businessRisk}</p>
                              </div>
                            )}
                            {/* G323 — backend emits `remediations`, not `recommendedActions`.
                                Accept both for legacy compat. */}
                            {(() => {
                              const actions = Array.isArray(a.remediations) ? a.remediations
                                          : Array.isArray(a.recommendedActions) ? a.recommendedActions
                                          : [];
                              if (actions.length === 0) return null;
                              return (
                                <div>
                                  <span className="text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted block mb-1">
                                    Recommended actions
                                  </span>
                                  <ul className="space-y-1.5 m-0 p-0 list-none">
                                    {actions.map((act, j) => {
                                      const text = typeof act === 'string' ? act
                                                 : (act.text || act.description || act.action || act.recommendation);
                                      const priority = typeof act === 'object' ? (act.priority || act.severity) : null;
                                      return (
                                        <li key={j} className="flex items-start gap-2 text-[12.5px]">
                                          {priority && (
                                            <Pill tone={(priority === 'HIGH' || priority === 'CRITICAL') ? 'danger'
                                                       : priority === 'MEDIUM' ? 'warning'
                                                       : 'cyan'} size="sm">
                                              {priority}
                                            </Pill>
                                          )}
                                          <span className="text-v2-ink">{text}</span>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              );
                            })()}
                            {/* G323 — backend boolean fields: anomalyDetected, probationActive */}
                            <div className="flex items-center gap-2 mt-3 flex-wrap">
                              {a.anomalyDetected && (
                                <Pill tone="warning" size="sm" dot>
                                  <AlertTriangle size={10} className="inline -mt-0.5 mr-0.5" />
                                  Anomaly detected{a.anomalyType && <> · {a.anomalyType.replace(/_/g, ' ').toLowerCase()}</>}
                                </Pill>
                              )}
                              {(a.probationActive ?? a.onProbation) && (
                                <Pill tone="danger" size="sm" dot>On probation</Pill>
                              )}
                              {a.recommendedAutonomy && a.recommendedAutonomy !== (a.autonomyLevel || a.autonomy) && (
                                <Pill tone="cyan" size="sm">
                                  Recommend → {a.recommendedAutonomy.replace(/_/g, ' ').toLowerCase()}
                                </Pill>
                              )}
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card.Body>
          </Card>

          {/* Fleet-level recommendations */}
          <Card padding="md">
            <Card.Header
              title="Fleet-level recommendations"
              subtitle="Actions that benefit the entire agent fleet, not a single agent"
            />
            <Card.Body>
              {recommendations.length === 0 ? (
                <EmptyState
                  icon={<CheckCircle2 size={20} />}
                  title="Fleet in good standing"
                  description="No actionable recommendations at this time."
                />
              ) : (
                <ul className="space-y-2 m-0 p-0 list-none">
                  {recommendations.map((r, i) => (
                    <li key={i} className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-3 flex items-start gap-3">
                      <Pill
                        tone={(r.priority === 'HIGH' || r.priority === 'CRITICAL') ? 'danger'
                            : r.priority === 'MEDIUM' ? 'warning'
                            : r.priority === 'LOW' ? 'success'
                            : 'cyan'}
                        size="sm"
                      >
                        {r.priority || 'INFO'}
                      </Pill>
                      <div className="flex-1 min-w-0">
                        {r.category && (
                          <div className="text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-0.5">
                            {r.category}
                          </div>
                        )}
                        <p className="text-[12.5px] text-v2-ink m-0">{r.text || r.recommendation || r.description}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card.Body>
          </Card>

          {/* Risk definitions table */}
          {riskDefinitions.length > 0 && (
            <Card padding="md">
              <Card.Header
                title="Risk level definitions"
                subtitle="What each level means and the expected operator response"
              />
              <Card.Body>
                <table className="w-full text-[12.5px]">
                  <thead>
                    <tr className="border-b border-v2-border-subtle">
                      <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2 pr-3">Level</th>
                      <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2 pr-3">What it means</th>
                      <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Recommended action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {riskDefinitions.map((d, i) => (
                      <tr key={i} className="border-b border-v2-border-subtle/50">
                        <td className="py-2 pr-3">
                          <Pill tone={d.level === 'CRITICAL' || d.level === 'HIGH' ? 'danger'
                                    : d.level === 'MEDIUM' ? 'warning'
                                    : 'success'} size="sm" dot>
                            {d.level}
                          </Pill>
                        </td>
                        <td className="py-2 pr-3 text-v2-ink">{d.meaning || d.description || '—'}</td>
                        <td className="py-2 text-v2-ink-secondary">{d.recommendedAction || d.action || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function QuickMetric({ label, value, tone = 'default' }) {
  const VALUE_TONE = {
    default: 'text-v2-ink',
    success: 'text-v2-success-strong',
    warning: 'text-v2-warning-strong',
    danger:  'text-v2-danger-strong',
    muted:   'text-v2-ink-muted',
  };
  return (
    <div className="bg-v2-surface-muted rounded-v2-sm p-3">
      <div className="text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted">{label}</div>
      <div className={`text-[20px] font-bold mt-0.5 ${VALUE_TONE[tone]}`}>{value}</div>
    </div>
  );
}

function GoalCell({ icon: Icon, label, value }) {
  return (
    <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-sm p-3 flex items-start gap-2.5">
      <Icon size={16} className="text-v2-action-strong shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted">{label}</div>
        <div className="text-[14px] font-bold text-v2-ink mt-0.5 truncate">{value}</div>
      </div>
    </div>
  );
}

function ExportOption({ label, description, onClick }) {
  return (
    <button onClick={onClick}
            className="w-full text-left px-3.5 py-2.5 hover:bg-v2-surface-muted border-b last:border-b-0 border-v2-border-subtle">
      <div className="font-semibold text-[12.5px] text-v2-ink flex items-center gap-2">
        <FileText size={12} className="text-v2-action-strong" />
        {label}
      </div>
      <div className="text-[10.5px] text-v2-ink-muted mt-0.5">{description}</div>
    </button>
  );
}

/**
 * ExecutiveSummary — render the narrative text the backend composes.
 * Supports two shapes:
 *   string                 — render as paragraphs split on \n\n
 *   { sections: [...] }    — { heading, body } pairs
 */
function ExecutiveSummary({ text }) {
  if (text == null) return null;
  if (typeof text === 'object' && Array.isArray(text.sections)) {
    return (
      <div className="space-y-3 text-[13px] text-v2-ink leading-relaxed">
        {text.sections.map((s, i) => (
          <div key={i}>
            <h4 className="text-[12.5px] font-bold text-v2-ink m-0 mb-1.5 uppercase tracking-wider">
              {s.heading || s.title}
            </h4>
            <p className="m-0">{s.body || s.text}</p>
          </div>
        ))}
      </div>
    );
  }
  const paragraphs = String(text).split(/\n\n+/g);
  return (
    <div className="space-y-3 text-[13px] text-v2-ink leading-relaxed">
      {paragraphs.map((p, i) => <p key={i} className="m-0">{p}</p>)}
    </div>
  );
}

export default BoardReportTab;
